/* eslint-disable no-unused-vars */
<template>
  <div class="sfdc">
    <div class="sfdc__title-area">
      <h3>Create disputes</h3>
      <span class="sfdc__title-area__sub-title">
        Search for invoice to create a new dispute
      </span>
    </div>
    <template v-if="!isEmpty(bulkCreatedId)">
      <Notification
        :title="'Your Bulk Dispute ' + bulkCreateStatus"
        icon="check-circle"
        type="success"
        class="sfdc__create-bulk-notification">
        <span>
          Your create bulk dispute is {{ bulkCreateStatus }} for create bulk Id
          {{ bulkCreatedId }}
        </span>
      </Notification>
    </template>
    <div class="sfdc__tabs">
      <Tabs :items="sfdcTabItems" @onClick="onTabActivated" />
    </div>
    <div class="sfdc__sub-tabs">
      <div
        v-if="currentSelectedTab === 'search-invoice'"
        class="sfdc__sub-tabs__search-invoice">
        <span>Search for invoices</span>
        <div class="sfdc__sub-tabs__search-invoice__search-area">
          <div class="sfdc__sub-tabs__search-invoice__search-area__content">
            <div
              class="sfdc__sub-tabs__search-invoice__search-area__content__text-input">
              <text-input
                id="invoice-input"
                icon="magnifying-glass"
                :show-clear-button="true"
                :value="invoiceIds"
                :label-sr-only="true"
                :error="isInvoiceError"
                :error-message="invoiceError"
                placeholder="Enter invoice numbers by comma seperated values"
                @onChange="onSearch"
                @onClear="onClear"
                @keydown="fetchInvoiceData" />
            </div>
            <Button
              label="Search invoices"
              class="invoice-input-button"
              appearance="primary"
              :disabled="isButtonDisabled"
              @onClick="fetchInvoiceData" />
          </div>
        </div>
      </div>
      <div
        v-if="currentSelectedTab === 'upload-excel' && !sfdcTableValues.length"
        class="sfdc__sub-tabs__upload-excel">
        <upload-attachment
          title="Upload Excel"
          :multiple="false"
          :maximum="1"
          :is-instant-upload="instantUpload"
          :supported-file-types="[
            'application/vnd.ms-excel',
            'text/csv',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'message/rfc822',
          ]"
          supported-file-format-message="Supported file formats: xlxs, xls, csv, eml"
          @uploadFiles="uploadFiles" />
      </div>
      <div v-if="currentSelectedTab === 'archived-invoices'">
        <archived-invoices-upload
          :is-bulk-status-modal-visible="isBulkStatusModalVisible"
          @setBulkCreatedId="setBulkCreatedId" />
      </div>

      <div v-if="splitInvoiceIds.length" class="sfdc__sub-tabs__configure-tags">
        <InvoiceTags
          :uploaded="currentSelectedTab === 'upload-excel'"
          :split-invoice-ids="splitInvoiceIds"
          @onRemove="onRemoveTag" />
        <Button
          v-if="currentSelectedTab === 'upload-excel'"
          v-tooltip="`Re-Upload excel`"
          appearance="tertiary"
          icon="tray-arrow-up"
          icon-position="left"
          icon-only
          @onClick="clearAll" />
      </div>

      <div
        v-if="notFoundBulkIds && notFoundBulkIds.length"
        class="sfdc__sub-tabs__not-found">
        <Notification
          type="error"
          title="Not found bulkids:"
          icon="times-circle"
          @handleIconClick="handleClickForNotFoundBulkids">
          <span>{{ notFoundBulkIds.toString() }}</span>
        </Notification>
      </div>

      <div v-if="splitInvoiceIds.length" class="sfdc__sub-tabs__not-found">
        <Notification
          title="Dispute creation is not supported if:"
          icon="info-circle"
          type="info"
          class="dispute-bulk__info">
          <span>
            Invoice is cancelled, Company Code is 300, Billing type is Z029,
            Z005, Z020, Z007, CF ,CD, VS, CM
          </span>
        </Notification>
      </div>

      <div
        v-if="
          notFoundInvoices && notFoundInvoices.length && sfdcTableValues.length
        "
        class="sfdc__sub-tabs__not-found">
        <Notification
          type="error"
          icon="times-circle"
          @handleIconClick="handleNotificationClick">
          <span>Not found invoices: {{ notFoundInvoices.toString() }}</span>
        </Notification>
      </div>
      <div
        v-if="
          operationFailedInvoices &&
          operationFailedInvoices.length &&
          sfdcTableValues.length
        "
        class="sfdc__sub-tabs__failed">
        <Notification
          type="error"
          title="Operation failed for invoices:"
          icon="times-circle"
          @handleIconClick="handleNotificationClick">
          <span>{{ operationFailedInvoices.toString() }}</span>
        </Notification>
      </div>

      <div v-if="invoiceRecordsLoading">
        <loading
          :active.sync="invoiceRecordsLoading"
          :can-cancel="false"
          :is-full-page="true"
          color="#0573ab" />
      </div>
      <div
        v-if="sfdcTableValues.length"
        class="sfdc__sub-tabs__table-conatiner">
        <SfdcTable
          :sfdc-table-columns="sfdcTableColumns"
          :sfdc-table-values="sfdcTableValues"
          :table-action-columns="tableActionColumns"
          :dispute-reasons="disputeReasons"
          :selected-rows="selectedRows"
          :selected-row-by-ids="selectedRowByIds"
          :active-row="activeRow"
          @updateInvoice="onUpdateInvoice"
          @updateActiveRow="updateActiveRow"
          @onMultiSelectInvoice="onMultiSelectInvoice"
          @onSelectDisputeReason="onSelectDisputeReason"
          @onCommentsUpdate="onCommentsUpdate"
          @onTextInputUpdate="onTextInputUpdate" />
      </div>
      <div v-if="selectedRows.length" class="sfdc__sub-tabs__bulk-update">
        <span>{{ `${selectedRows.length} invoices selected` }}</span>
        <Button
          :label="bulkUpdateButtonLabel"
          appearance="primary"
          icon="arrow-right"
          icon-position="right"
          :disabled="!selectedRowByIds.length || bulkButtonDisable"
          @onClick="onBulkUpdate" />
      </div>
    </div>
    <bulk-dispute-modal
      v-if="!isEmpty(bulkCreatedId) || isBulkStatusModalVisible"
      :enable-search="isEmpty(bulkCreatedId)"
      @onModalClose="onModalClose">
    </bulk-dispute-modal>
  </div>
</template>

<script>
// Constants
import {
  sfdcTabItems,
  sfdcTableColumns,
  excelKeyMapping,
  closedDisputeStatusCodes,
  lookUpParameters,
} from "@/config/sfdcConstant.js";
import { tableActionColumns } from "@/config/constant.js";
// axios
import AxiosFetchForMsal from "@/utils/axiosForMsal";
// Actions
import { disputeActions } from "@/actions";
//helpers
import { getMsalIdToken, resolutionDropdownFields } from "@/utils/helpers";
// Custom Components
import InvoiceTags from "@/components/Sfdc/InvoiceTags";
import SfdcTable from "@/components/Sfdc/SfdcTable";
import { showToast } from "@/components/common/Toast";
import Loading from "vue-loading-overlay";
import Notification from "@anchor/ui-components/src/components/Notification/Notification.vue";
import BulkDisputeModal from "@/components/BulkDisputeModal/BulkDisputeModal.vue";
import ArchivedInvoicesUpload from "@/components/Sfdc/ArchivedInvoicesUpload.vue";

import { isEmpty } from "@/utils/helpers.js";
// Package: SheetJs(Excel to JSON convertor)
import * as XLSX from "xlsx";

//vuex
import { mapGetters } from "vuex";

export default {
  name: "Sfdc",
  components: {
    InvoiceTags,
    SfdcTable,
    Loading,
    Notification,
    BulkDisputeModal,
    ArchivedInvoicesUpload,
  },
  props: {
    isBulkStatusModalVisible: {
      type: Boolean,
      default: false,
      required: true,
    },
  },

  data() {
    return {
      sfdcTabItems,
      isEmpty,
      sfdcTableColumns,
      sfdcTableValues: [],
      tableActionColumns,
      disputeReasons: null,
      currentSelectedTab: "search-invoice",
      invoiceIds: "",
      splitInvoiceIds: [],
      isInvoiceError: false,
      invoiceError: "",
      isBulkError: false,
      notFoundInvoices: [],
      notFoundBulkIds: [],
      selectedReason: "",
      getSortOptions: [
        {
          label: "Last updated",
          value: "Last updated",
        },
        {
          label: "Most urgent to least",
          value: "Most urgent to least",
        },
      ],
      selectedSortOption: {},
      selectedRows: [],
      selectedRowByIds: [],
      invoiceRecordsLoading: false,
      invoiceRecordsError: false,
      activeRow: {},
      disputesApiData: [],
      bulkUpdateButtonLabel: "Create/Update bulk dispute",
      instantUpload: true,
      operationFailedInvoices: [],
      bulkButtonDisable: false,
      pollingInterval: null,
      bulkIds: "",
      bulkCreatedId: localStorage.getItem("bulk_create_id"),
      bulkUpdatedInvoices: [],
      bulkFailedInvoices: [],
      bulkProcessingCompleted: false,
      uploadedFiles: null,
    };
  },
  computed: {
    ...mapGetters(["getIsIndexer"]),
    isButtonDisabled() {
      return !this.invoiceIds?.length ? true : false;
    },
    bulkCreateStatus() {
      return this.bulkProcessingCompleted ? "completed" : "in progress";
    },
  },
  watch: {
    selectedRows() {
      const bulkMenuArr = [
        ...new Set(
          this.selectedRows.map((row) => {
            return row?.statusCode ? "Update" : "Create";
          })
        ),
      ];
      this.bulkUpdateButtonLabel = `${bulkMenuArr.join(" / ")} bulk dispute`;
      this.clearErrorWhenThereIsAChange();
    },
    sfdcTableValues() {
      this.sfdcTableValues.forEach((item) => {
        if (this.selectedRowByIds.includes(item.invoiceNumber)) {
          const findIndex = this.selectedRows.findIndex(
            (el) => el.invoiceNumber == item.invoiceNumber
          );
          this.selectedRows[findIndex] = item;
        }
      });
    },
  },
  created() {
    if (!this.getIsIndexer) {
      showToast("error", "Unauthorized to access this page");
      this.$router.push("/");
    }
  },
  mounted() {
    this.fetchDisputeReasons();
    if (!isEmpty(localStorage.getItem("bulk_create_id"))) {
      this.bulkCreatedId = localStorage.getItem("bulk_create_id");
      setTimeout(() => {
        this.bulkCreatedId = null;
        localStorage.removeItem("bulk_create_id");
      }, 1000 * 60 * 5);
    }
  },
  methods: {
    setBulkCreatedId(bulkCreatedId) {
      this.bulkCreatedId = bulkCreatedId;
    },

    onModalClose() {
      if (isEmpty(this.bulkCreatedId)) {
        this.$emit("toggleBulkStatusModal");
      } else {
        if (this.currentSelectedTab === "search-invoice") {
          this.fetchInvoiceData();
        } else if (this.currentSelectedTab === "upload-excel") {
          if (this.uploadedFiles.length > 0)
            this.parseExcel(this.uploadedFiles);
        }
        localStorage.removeItem("bulk_create_id");
        this.bulkCreatedId = "";
      }
    },
    clearErrorWhenThereIsAChange() {
      if (!this.sfdcTableValues?.some((item) => item.error === true))
        return false;
      let sfdcTableValues = this.sfdcTableValues.map((item) => {
        return {
          ...item,
          error: false,
          errorMessage: "",
        };
      });
      this.operationFailedInvoices = [];
      this.sfdcTableValues = sfdcTableValues;
    },
    uploadFiles(uploadedFiles) {
      this.uploadedFiles = uploadedFiles;
      if (uploadedFiles.length > 0) this.parseExcel(uploadedFiles);
    },

    async parseExcel(uploadedFiles) {
      // Converting excel to json
      if (uploadedFiles) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const json = XLSX.utils.sheet_to_json(worksheet);
          if (json.length) {
            // Function to process & populates table from JSON
            this.processExcel(json);
          } else {
            showToast("error", "No Data found in Document");
            this.onAttachmentUploadFail();
            return false;
          }
        };
        reader.readAsArrayBuffer(uploadedFiles[0].file);
      }
    },
    onAttachmentUploadFail() {
      this.instantUpload = false;
      this.invoiceRecordsLoading = false;
    },
    async processExcel(json) {
      this.invoiceRecordsLoading = true;
      this.instantUpload = true;
      // Stops execution when record length is more than maxlen(250)
      if (json.length > 250) {
        showToast("error", "Limit exceeded. Max number of record is 250");
        this.onAttachmentUploadFail();
        return false;
      }
      let excelObj = {};
      // Transforming excel schema to corresponding table key's
      json.forEach((record) => {
        let convertedObj = {};
        Object.entries(record).forEach(([key, value]) => {
          convertedObj = {
            ...convertedObj,
            [excelKeyMapping[key]]: value,
          };
        });
        excelObj = {
          ...excelObj,
          [convertedObj.invoiceNumber]: {
            ...convertedObj,
            reasonCode: this.disputeReasons.find(
              (reason) =>
                reason?.displayName === convertedObj?.reasonDescription
            )?.id,
          },
        };
      });
      // Stops execution when there is not invoice number in the uploaded excel
      if (!Object.keys(Object.values(excelObj)[0]).includes("invoiceNumber")) {
        showToast("error", "Unable to parse. Please check the schema");
        this.onAttachmentUploadFail();
        return false;
      }

      // Validating, fetching & updating invoice details
      let { invoiceDisputeData } = await this.fetchInvoiceDisputeDetails(
        Object.keys(excelObj),
        false,
        excelObj
      );

      // Stops execution when response is empty in the uploaded excel
      if (this.disputesApiData.length < 1) {
        showToast("error", "No data found");
        this.onAttachmentUploadFail();
        return false;
      }

      this.sfdcTableValues = Object.values(invoiceDisputeData);
      this.invoiceRecordsLoading = false;
    },
    /**
     * @param: fromSeach is true when fn call is from fetchInvoiceData
     */
    async fetchInvoiceDisputeDetails(invoiceArr, fromSearch = false, excelObj) {
      let eccInvoiceArr = [];
      let s4InvoiceArr = [];
      // Sorting ECC & S4 invoices
      invoiceArr?.forEach((invoiceNumber) => {
        if (/^[59].*$/i.test(invoiceNumber)) {
          eccInvoiceArr.push(invoiceNumber);
        } else {
          s4InvoiceArr.push(invoiceNumber);
        }
      });
      const invoiceFetch = [
        {
          type: "ecc",
          invoice: eccInvoiceArr,
        },
        {
          type: "s4",
          invoice: s4InvoiceArr,
        },
        {
          type: "dispute",
          invoice: [...eccInvoiceArr, ...s4InvoiceArr],
        },
      ];
      const msalToken = await getMsalIdToken(this);
      let promiseArray = [];
      //  Creating multiple API call's as one call can only have maximum of 25 invoice number
      invoiceFetch.forEach((item) => {
        const invoiceArr = item.invoice;
        const isDispute = item.type === "dispute";
        const INVOICE_LIMIT = 25;
        const noOfApiCalls = invoiceArr.length / INVOICE_LIMIT;
        for (let i = 0; i < noOfApiCalls; i++) {
          const endSlice =
            invoiceArr.length < (i + 1) * INVOICE_LIMIT
              ? invoiceArr.length
              : (i + 1) * INVOICE_LIMIT;
          const startSlice = i * INVOICE_LIMIT;
          if (isDispute) {
            // API to get details for already created invoice
            promiseArray.push(
              disputeActions.getDisputeDetailsFromInvoiceId(
                msalToken,
                invoiceArr?.slice(startSlice, endSlice).toString(),
                0,
                200
              )
            );
          } else {
            // API to validate invoice and fetch details
            promiseArray.push(
              disputeActions.getDisputeInvoiceDetailsFromSap(
                msalToken,
                invoiceArr?.slice(startSlice, endSlice).toString(),
                0,
                2000
              )
            );
          }
        }
      });

      let invoiceList = [];
      let disputeList = [];
      let hasError = false;
      let errorMessage = "";
      let onlyAPIdata = {};
      // Parallel API call's
      await Promise.allSettled(
        promiseArray?.map((endpoint) => AxiosFetchForMsal(endpoint))
      )
        .then((res) => {
          res.forEach((item) => {
            if (item.status === "rejected") {
              hasError = true;
              errorMessage = item.reason;
              return false;
            }
            const data = item?.value?.data || [];
            // SAP response
            if (Object.keys(data).includes("invoiceList")) {
              invoiceList.push(...data.invoiceList);
            }
            //Dispute api response
            if (Object.keys(data).includes("content")) {
              disputeList.push(...data.content);
            }
          });
        })
        .catch((e) => {
          if (!fromSearch) {
            this.onAttachmentUploadFail();
          }
          console.error("e", e);
          showToast("error", "Failed to fetch data");
          return false;
        });
      // Stops funciton execution if any of the API fails
      if (hasError) {
        showToast("error", errorMessage);
        if (!fromSearch) this.onAttachmentUploadFail();
        return false;
      }
      let invoiceDisputeData = {};
      invoiceList?.forEach((invoice) => {
        const findIndex = disputeList?.findIndex((dispute) => {
          return dispute?.invoiceNumber == invoice?.invoiceNumber;
        });
        const key = !fromSearch
          ? invoice.invoiceNumber
          : invoice.odn && invoice.odn !== ""
          ? invoice.odn
          : invoice.invoiceNumber;

        if (!Object.keys(invoiceDisputeData).includes(key)) {
          if (findIndex > -1) {
            // Extract sfdcCaseId from sfdcCaseDetails if available
            let sfdcCaseId = "";
            const sfdcCaseDetails = disputeList[findIndex]?.sfdcCaseDetails;
            if (sfdcCaseDetails) {
              try {
                const parsed = JSON.parse(sfdcCaseDetails);
                if (parsed?.[0]?.caseId) {
                  sfdcCaseId = parsed[0].caseId;
                }
              } catch (e) {
                console.log("Error parsing sfdcCaseDetails:", e);
              }
            }
            // Updating already created invoice details
            invoiceDisputeData = {
              ...invoiceDisputeData,
              [key]: {
                ...invoice,
                ...disputeList[findIndex],
                statusCodeDesc: `${disputeList[findIndex].statusCode}: ${disputeList[findIndex].statusDescription}`,
                ...(sfdcCaseId && { sfdcCaseId }),
              },
            };
            onlyAPIdata = {
              ...onlyAPIdata,
              [key]: {
                ...invoice,
                ...disputeList[findIndex],
                ...(sfdcCaseId && { sfdcCaseId }),
              },
            };
          } else {
            // Adding record in the table for new invoice
            invoiceDisputeData = {
              ...invoiceDisputeData,
              [key]: { ...invoice },
            };
            onlyAPIdata = {
              ...onlyAPIdata,
              [key]: { ...invoice },
            };
          }
          if (!fromSearch) {
            // Updating corresponding column values which are from excel
            const newExcelObj = Object.entries(excelObj).reduce(
              (acc, [originalKey, invoice]) => {
                acc[originalKey] = {
                  ...invoice,
                  invoiceNumber: originalKey,
                };

                return acc;
              },
              {}
            );

            let excelDataToMerge =
              newExcelObj[invoice.invoiceNumber] || newExcelObj[invoice.odn];
            invoiceDisputeData = {
              ...invoiceDisputeData,
              [key]: {
                ...invoiceDisputeData[key],
                ...(excelDataToMerge || {}),
                invoiceNumber: invoice.invoiceNumber, // Always set to Invoice API invoice number
              },
            };
          }
        }
      });

      let notFoundInvoice = [];
      invoiceArr.forEach((key) => {
        // Check if the key exists as an odn or invoiceNumber
        const odnKey = Object.values(invoiceDisputeData).find(
          (item) =>
            item.odn === key || (!fromSearch && item.invoiceNumber === key)
        );
        const invoiceKey = invoiceDisputeData[key];

        // If the key is an invoiceNumber, check if its odn exists
        const odnFromInvoice = Object.values(invoiceDisputeData).find(
          (item) => item.invoiceNumber === key && item.odn
        );

        if (!odnKey && !invoiceKey && !odnFromInvoice) {
          notFoundInvoice.push(" " + key);
        }
      });
      this.notFoundInvoices = notFoundInvoice;
      this.splitInvoiceIds = Object.keys(invoiceDisputeData);
      this.disputesApiData = Object.values(onlyAPIdata);
      return { invoiceDisputeData, invoiceList, disputeList };
    },
    invoiceNumberFieldCleanup() {
      let value = this.invoiceIds?.split(/\s+/)?.join(",") || "";
      let valueArr = value?.split(",")?.filter((item) => item) || [];
      value = valueArr.join(",");
      value = value.replace(/^,/, "");
      value = value.replace(/,$/, "");
      return value;
    },
    async fetchInvoiceData() {
      this.sfdcTableValues = [];
      this.splitInvoiceIds = [];
      this.notFoundInvoices = [];
      this.selectedRows = [];
      this.selectedRowByIds = [];
      this.invoiceRecordsLoading = true;
      try {
        // Cleaning invoice nuumbers in search bar
        this.invoiceIds = this.invoiceNumberFieldCleanup();
        const newInvoiceIds = this.invoiceIds.split(",");
        if (newInvoiceIds.length > 250) {
          showToast("error", "Invoice searching is limited to 250 invoices");
          return false;
        }
        // Fetching Invoice details from SAP and Dispute API's
        let { invoiceDisputeData = {} } = await this.fetchInvoiceDisputeDetails(
          newInvoiceIds,
          true
        );
        const newSfdcTableValues = Object.values(invoiceDisputeData);
        this.sfdcTableValues = [
          ...new Set(this.sfdcTableValues?.concat(newSfdcTableValues)),
        ];
        if (this.disputesApiData.length === 0) {
          showToast("error", "No data found");
        }
      } catch (err) {
        showToast(
          "error",
          `${err || "Unable to get Invoices, please try again!"}`
        );
      } finally {
        this.invoiceRecordsLoading = false;
      }
    },
    updateActiveRow(row) {
      this.activeRow = row;
    },
    async fetchDisputeReasons() {
      try {
        const { data } = await AxiosFetchForMsal(
          disputeActions.disputeFieldConstants(await getMsalIdToken(this))
        );
        this.disputeReasons = resolutionDropdownFields(data, true)[
          "REASON"
        ]?.slice(1);
      } catch (error) {
        console.error(error);
      }
    },
    onTabActivated({ selectedItemId, updatedTabData }) {
      this.clearAll();
      this.currentSelectedTab = selectedItemId;
      this.sfdcTabItems = updatedTabData;
    },
    onSearch(e) {
      this.invoiceIds = e.target.value;
    },
    onClear() {
      if (this.invoiceIds || this.bulkIds) {
        this.invoiceIds = "";
        this.isInvoiceError = false;
        this.invoiceError = "";
        this.selectedRows = [];
        this.selectedRowByIds = [];
      }
    },
    clearAll() {
      this.onClear();
      this.sfdcTableValues = [];
      this.notFoundInvoices = [];
      this.splitInvoiceIds = [];
      this.selectedRows = [];
      this.selectedRowByIds = [];
      this.notFoundBulkIds = [];
    },
    handleNotificationClick() {
      this.notFoundInvoices = [];
    },
    handleClickForNotFoundBulkids() {
      this.notFoundBulkIds = [];
    },
    onRemoveTag(invoiceId) {
      this.clearErrorWhenThereIsAChange();
      const splitInvoiceIds = [...this.splitInvoiceIds];
      const sfdcTableValues = [...this.sfdcTableValues];
      const selectedIndex = this.selectedRows.findIndex(
        (item) => item.invoiceNumber == invoiceId
      );
      if (selectedIndex > -1) this.selectedRows.splice(selectedIndex, 1);
      const index = splitInvoiceIds.indexOf(invoiceId);
      const invoiceIndex = sfdcTableValues.findIndex(
        (item) => Number(item?.invoiceNumber) == invoiceId
      );
      if (index > -1 && invoiceIndex > -1) {
        splitInvoiceIds.splice(index, 1);
        sfdcTableValues.splice(invoiceIndex, 1);
        this.splitInvoiceIds = splitInvoiceIds;
        this.sfdcTableValues = sfdcTableValues;
      }
      this.activeRow =
        this.activeRow?.invoiceNumber === invoiceId ? {} : this.activeRow;
    },
    onMultiSelectInvoice(selectedItems) {
      this.selectedRows = [...selectedItems];
      if (this.selectedRows?.length) {
        this.selectedRowByIds = this.selectedRows.map(
          (item) => item.invoiceNumber
        );
      } else {
        this.selectedRowByIds = [];
      }
    },
    onSelectDisputeReason(value, row) {
      this.clearErrorWhenThereIsAChange();
      const sfdcTableValues = [...this.sfdcTableValues];
      const findIndex = sfdcTableValues.findIndex(
        (item) => item?.invoiceNumber === row?.invoiceNumber
      );
      if (findIndex > -1) {
        sfdcTableValues[findIndex] = {
          ...sfdcTableValues[findIndex],
          reasonDescription: value?.value,
          reasonCode: value?.id,
        };
      }
      this.sfdcTableValues = sfdcTableValues;
    },
    onCommentsUpdate(event, row) {
      this.clearErrorWhenThereIsAChange();
      const sfdcTableValues = [...this.sfdcTableValues];
      const findIndex = sfdcTableValues.findIndex(
        (item) => item?.invoiceNumber === row?.invoiceNumber
      );
      if (findIndex > -1) {
        sfdcTableValues[findIndex] = {
          ...sfdcTableValues[findIndex],
          noteDescription: event,
        };
      }
      this.sfdcTableValues = sfdcTableValues;
    },
    onTextInputUpdate(event, row, slotKey) {
      this.clearErrorWhenThereIsAChange();
      const sfdcTableValues = [...this.sfdcTableValues];
      const findIndex = sfdcTableValues.findIndex(
        (item) => item?.invoiceNumber === row?.invoiceNumber
      );
      if (findIndex > -1) {
        sfdcTableValues[findIndex] = {
          ...sfdcTableValues[findIndex],
          [slotKey]: event,
        };
      }
      this.sfdcTableValues = sfdcTableValues;
    },
    async handleBulkCommentAPICalls(bulkComments) {
      const MAX_LIMIT = 25;
      const bulkApiNoOfCalls = bulkComments.length / MAX_LIMIT;
      const bulkPromise = [];
      const msalToken = await getMsalIdToken(this);
      for (let i = 0; i < bulkApiNoOfCalls; i++) {
        const endSlice =
          bulkComments.length < (i + 1) * MAX_LIMIT
            ? bulkComments.length
            : (i + 1) * MAX_LIMIT;
        const startSlice = i * MAX_LIMIT;
        bulkPromise.push(
          disputeActions.saveBulkSFDCComments(
            bulkComments.slice(startSlice, endSlice),
            msalToken
          )
        );
      }
      return bulkPromise;
    },
    createDisputePayload(row) {
      if (row) {
        return {
          customer: row.customerNumber,
          contactEmail: row.contactEmail,
          noteDescription: row.noteDescription,
          reasonCode: row.reasonCode,
          reasonDescription: row.reasonDescription,
          sfdcCaseId: row.sfdcCaseId,
          invoiceNumber: row.invoiceNumber,
          odn: row.odn,
          areaCode: row.areaCode,
          areaName: row.areaName,
          billingDocumentDate: row.billingDate,
          businessAreaCode: row.businessAreaCode,
          companyCode: row.CompanyCode,
          companyDescription: row.companyDescription,
          countryCode: row.countryCode,
          countryName: row.countryName,
          currency: row.currencyGcss,
          documentDate: row.documentDate,
          documentType: row.documentType,
          invoiceAmount: row.invoiceAmount,
          localCurrency: row.currencyLocal,
          referenceKey: row.referenceKey,
          regionCode: row.regionCode,
          regionName: row.regionName,
          tpDocumentNumber: row.tpdoc,
          contactPerson: row.contactPerson,
        };
      }
      return {};
    },
    async onBulkUpdate() {
      this.clearErrorWhenThereIsAChange();
      this.invoiceRecordsLoading = true;
      let bulkCreateDisputes = [];
      let bulkUpdateDisputes = [];
      if (this.bulkUpdateButtonLabel.includes("Create")) {
        this.selectedRows.forEach((row) => {
          if (!row?.disputeStage && !row?.statusCode) {
            bulkCreateDisputes.push(this.createDisputePayload(row));
          }
        });
        try {
          const { data } = await AxiosFetchForMsal(
            disputeActions.bulkDisputeCreate(
              { data: bulkCreateDisputes },
              await getMsalIdToken(this)
            )
          );
          showToast("success", `Success`);
          localStorage.setItem("bulk_create_id", data);
          this.bulkCreatedId = data;
        } catch (err) {
          showToast(
            "error",
            `Failed to bulk ${
              this.bulkUpdateButtonLabel.includes("Update")
                ? "Update"
                : "Create"
            }. Please try again!`
          );
        }
      }
      const apiData = this.disputesApiData;
      let bulkCommentInvoiceObj = {};
      if (this.bulkUpdateButtonLabel.includes("Update")) {
        this.selectedRows.forEach((row) => {
          let rowValue = {};
          if (row?.statusCode) {
            const parentRow = apiData.find(
              (item) => item.invoiceNumber == row.invoiceNumber
            );
            const changedObjects = Object.keys(parentRow).filter(
              (key) => parentRow[key] !== row[key]
            );
            changedObjects.forEach((item) => {
              rowValue = {
                ...rowValue,
                [item]: row[item],
              };
            });
            if (
              closedDisputeStatusCodes.includes(row?.statusCode) &&
              row.disputeStage !== "Redispute"
            ) {
              rowValue = {
                ...rowValue,
                disputeStage: "Redispute",
                statusCode: "75",
              };
            }
            bulkUpdateDisputes.push({
              ...rowValue,
              ohpDisputeId: row?.ohpDisputeId,
              invoiceNumber: row?.invoiceNumber,
            });
            if (changedObjects.includes("noteDescription")) {
              bulkCommentInvoiceObj = {
                ...bulkCommentInvoiceObj,
                [row.invoiceNumber]: {
                  ohpDisputeId: row.ohpDisputeId,
                  commentText: row.noteDescription,
                  commentHtml: row.noteDescription,
                  createdSource: "DV",
                  isExternal: false,
                },
              };
            }
          }
        });
        try {
          const { data } = await AxiosFetchForMsal(
            disputeActions.bulkDisputeUpdate(
              { data: bulkUpdateDisputes, commentsData: bulkCommentInvoiceObj },
              await getMsalIdToken(this)
            )
          );
          showToast("success", `Success`);
          localStorage.setItem("bulk_create_id", data);
          this.bulkCreatedId = data;
        } catch (err) {
          showToast(
            "error",
            `Failed to bulk ${
              this.bulkUpdateButtonLabel.includes("Update")
                ? "Update"
                : "Create"
            }. Please try again!`
          );
        }
      }
      this.clearSelectedRows();
      this.invoiceRecordsLoading = false;
    },
    clearSelectedRows() {
      this.selectedRows = [];
      this.selectedRowByIds = [];
    },
    async onUpdateInvoice(row) {
      this.bulkButtonDisable = true;
      this.clearErrorWhenThereIsAChange();
      const sfdcTableValues = [...this.sfdcTableValues];
      const findIndex = sfdcTableValues.findIndex(
        (item) => item?.invoiceNumber == row?.invoiceNumber
      );
      const apiData = this.disputesApiData;
      try {
        if (findIndex > -1) {
          sfdcTableValues[findIndex] = {
            ...sfdcTableValues[findIndex],
            loading: true,
          };
        }
        this.sfdcTableValues = [...sfdcTableValues];
        let payload = {
          ...this.createDisputePayload(row),
        };
        // create dispute flow
        if (!row?.ohpDisputeId && !row?.statusCode) {
          const { data } = await AxiosFetchForMsal(
            disputeActions.createDisputeFromSfdc(
              this.createDisputePayload(row),
              await getMsalIdToken(this)
            )
          );
          showToast("success", "Dispute created successfully");
          sfdcTableValues[findIndex] = {
            ...sfdcTableValues[findIndex],
            ohpDisputeId: data?.ohpDisputeId,
            statusCode: data?.statusCode,
            disputeStage: data?.disputeStage,
            statusCodeDesc: `${data?.statusCode}: ${data?.statusDescription}`,
            loading: false,
          };
          this.sfdcTableValues = [...sfdcTableValues];
        } else if (row?.ohpDisputeId && row?.statusCode) {
          // update and redispute workflow
          const isReDisputed = closedDisputeStatusCodes.includes(
            row?.statusCode
          );
          payload = {};
          if (isReDisputed && row.disputeStage !== "Redispute") {
            //redispute payload
            payload = {
              disputeStage: "Redispute",
              statusCode: "75",
            };
          }
          const parentRow = apiData.find(
            (item) => item.invoiceNumber == row.invoiceNumber
          );
          const changedObjects = Object.keys(parentRow).filter(
            (key) => parentRow[key] !== row[key]
          );
          changedObjects.forEach((item) => {
            payload = {
              ...payload,
              [item]: row[item],
            };
          });
          const { data } = await AxiosFetchForMsal(
            disputeActions.updateDisputeFromSfdc(
              payload,
              row.ohpDisputeId,
              await getMsalIdToken(this)
            )
          );
          let disputeData = [...this.disputesApiData];
          let addIndex = disputeData.findIndex(
            (item) => item.invoiceNumber == row.invoiceNumber
          );
          if (addIndex > -1) {
            const {
              disputeStage,
              noteDescription,
              statusCode,
              reasonDescription,
              contactPerson,
            } = data;
            disputeData[addIndex] = {
              ...disputeData[addIndex],
              disputeStage,
              noteDescription,
              statusCode,
              reasonDescription,
              contactPerson,
            };
            this.disputesApiData = disputeData;
          }
          showToast(
            "success",
            `Dispute ${isReDisputed ? "redisputed" : "updated"} successfully`
          );
          if (changedObjects.includes("noteDescription")) {
            // To be deprecated: as per the discussion with zishan update comments api will be calling internally in future
            try {
              await AxiosFetchForMsal(
                disputeActions.saveComments(
                  row.ohpDisputeId,
                  false,
                  {
                    commentText: payload["noteDescription"],
                    commentHtml: payload["noteDescription"],
                  },
                  `Bearer ${await getMsalIdToken(this)}`
                )
              );
            } catch (e) {
              showToast("error", "Failed to update comment. Please try again!");
            }
          }
          sfdcTableValues[findIndex] = {
            ...sfdcTableValues[findIndex],
            statusCode: data?.statusCode,
            disputeStage: data?.disputeStage,
            statusCodeDesc: `${data?.statusCode}: ${data?.statusDescription}`,
            loading: false,
          };
          this.sfdcTableValues = [...sfdcTableValues];
        }
      } catch (err) {
        if (findIndex > -1) {
          sfdcTableValues[findIndex] = {
            ...sfdcTableValues[findIndex],
            loading: false,
          };
        }
        this.sfdcTableValues = [...sfdcTableValues];
        showToast(
          "error",
          err?.response?.data?.errorMessage || "Unable to create dispute"
        );
      }
      this.clearSelectedRows();
      this.bulkButtonDisable = false;
    },
  },
};
</script>
<style lang="scss" scoped>
.sfdc {
  padding: 24px;
  display: flex;
  margin-top: 80px;
  flex-direction: column;
  @media (min-width: 1400px) {
    padding: 24px 48px;
  }
  .button--primary {
    max-height: 42px;
  }
  &__title-area {
    margin-top: 8px;
    h3 {
      font-family: "Maersk Headline";
      font-style: normal;
      font-weight: 400;
      font-size: 26px;
      line-height: 32px;
      color: var(--Gray-900);
    }
    &__sub-title {
      margin-top: 8px;
      font-family: "Maersk Text";
      font-style: normal;
      font-weight: 400;
      font-size: 16px;
      line-height: 24px;
      color: var(--Gray-700);
    }
  }
  &__tabs {
    margin-top: 16px;
  }
  &__sub-tabs {
    padding: 24px;
    min-height: 120px;
    background: var(--Gray-50);
    border: 1px solid #d8d8d8;
    border-top: none;
    box-shadow: 0 1px 5px rgb(0 0 0 / 4%), 0 3px 1px -2px rgb(0 0 0 / 4%),
      0 2px 2px rgb(0 0 0 / 4%);
    &__search-invoice {
      padding-bottom: 24px;
      span {
        font-family: "Maersk Headline";
        font-style: normal;
        font-weight: 400;
        font-size: 20px;
        line-height: 24px;
        color: var(--Blue-900-Darkest);
      }
      &__search-area {
        display: flex;
        justify-content: space-between;
        margin-top: 8px;
        &__content {
          display: flex;
          flex: 1;
          &__text-input {
            width: 70%;
          }
        }
      }
    }
    &__not-found,
    &__failed {
      padding-bottom: 20px;
      width: 70%;
      span {
        font-family: "Maersk Text";
        font-style: normal;
        font-weight: 400;
        font-size: 14px;
        line-height: 24px;
        color: var(--Blue-900-Darkest);
      }
    }
    &__bulk-update {
      display: flex;
      justify-content: space-between;
      margin-top: 12px;
      span {
        font-family: "Maersk Text";
        font-style: normal;
        font-weight: 400;
        font-size: 16px;
        line-height: 24px;
      }
    }
    &__configure-tags {
      display: flex;
      justify-content: space-between;
      a {
        min-width: 50px;
      }
    }
  }
  &__create-bulk-notification {
    margin-top: 16px;
  }
}
</style>
<style lang="scss">
.sfdc {
  #invoice-input {
    border-top-right-radius: 0px;
    border-bottom-right-radius: 0px;
  }
  .invoice-input-button {
    border-top-left-radius: 0px;
    border-bottom-left-radius: 0px;
  }
  .dispute-reason {
    .form-input__field {
      max-height: 40px !important;
      width: 180px;
    }
  }
  &__sub-tabs {
    &__search-invoice {
      &__search-area {
        &__right-content {
          & .form-group {
            display: flex;
            align-items: center;
            & .form-label {
              margin-bottom: 0;
              margin-right: 8px;
            }
          }
        }
      }
    }
  }
}
</style>
